// Composer Studio — 작성 탭 미리보기의 **상황 시뮬** 파생(순수 함수)
//
// 결정 2026-09-06 §3: create/edit 모드 시뮬을 상황 시뮬(생성 / 이어 작성 k/N / 마지막 / 수정)로
// 확장한다. 저장 버튼 라벨은 상황에 따라 갈리는데(카운터·전환 예고), 저작 화면에서 그것을 볼 수
// 없으면 저작자는 자기가 켠 선언이 무엇을 바꿨는지 모른 채 저장하게 된다.
//
// ## 이 모듈이 만드는 것은 `ComposeSubmitSituation`뿐이다
//
// 라벨 문자열을 만들지 **않는다** — 해소는 `resolveComposeSubmitLabel`(shared) 한 곳이고,
// 실화면과 에디터 미리보기가 같은 셸을 쓰므로 복제 드리프트가 구조적으로 0이다(§3). 여기서 또
// 라벨을 조립하면 그 성질이 깨진다.
//
// ## 두 축의 출처가 다르다
//
//  - **상황**(생성/이어/마지막/수정)은 시뮬 축이다 — 저작자가 고른다.
//  - **이어질 흐름 CTA**는 시뮬 축이 아니라 **편집 중 템플릿의 `policy.postSubmit`에서 파생**된다.
//    선언 효과를 눈으로 확인하는 것이 목적이라, 시뮬로 켤 수 있으면 확인이 되지 않는다.
//
// ⚠️ 여기의 「분할」은 미리보기 컨텍스트 바의 분할 그룹 모사 축(`PREVIEW_SIMULATION_SPLIT_AXIS_KEY`,
//    '어느 그룹의 데이터로 그릴까')과 **다른 축**이다. 이쪽은 '몇 번째 건을 저장하는 자리인가'다.
import type { ComposeSubmitSituation, TemplatePolicy } from '@safetyhub/shared';
import { findPostSubmitFlow } from '@safetyhub/shared';
import { getSplitAxisProvider } from './splitAxisPolicy';
import { getPostSubmitFlow } from './postSubmitFlowPolicy';

/** 시뮬할 상황 — 결정 §3 표의 행들(단건·중간 건·마지막 건·수정). */
export type PreviewSubmitSituationKey = 'create-single' | 'split-middle' | 'split-last' | 'edit';

/**
 * 분할 시뮬의 가짜 전체 건수. 3인 이유는 「(1/3)」이 **중간 건**임이 한눈에 보이는 최소 수라서다
 * (2면 1/2가 중간인지 마지막 직전인지 읽는 사람이 세어야 한다). 실제 건수는 작성 시점 데이터가
 * 정하므로 여기 값은 표시용이고, 저작자가 확인하려는 것은 숫자가 아니라 **접미의 형태**다.
 */
export const PREVIEW_SPLIT_SIMULATED_TOTAL = 3;

const OPTION_LABELS: Record<PreviewSubmitSituationKey, string> = {
  'create-single': '생성(단건)',
  'split-middle': `분할 이어 작성 (1/${PREVIEW_SPLIT_SIMULATED_TOTAL})`,
  'split-last': `분할 마지막 (${PREVIEW_SPLIT_SIMULATED_TOTAL}/${PREVIEW_SPLIT_SIMULATED_TOTAL})`,
  edit: '수정',
};

/**
 * 이 템플릿에서 **실제로 일어날 수 있는** 상황들.
 *
 * 분할 두 갈래는 `policy.split` 축이 선언된 템플릿에만 나온다 — 축이 없으면 작성은 언제나 한
 * 건이라 그 상황이 성립하지 않고, 일어날 수 없는 상황을 고르게 두면 시뮬이 거짓을 보인다.
 * (CTA를 시뮬 축으로 두지 않고 편집 중 정책에서 파생시키는 것과 같은 이유다.)
 */
export function previewSubmitSituationOptions(
  policy: TemplatePolicy | undefined,
): ReadonlyArray<{ value: PreviewSubmitSituationKey; label: string }> {
  const keys: PreviewSubmitSituationKey[] = getSplitAxisProvider(policy)
    ? ['create-single', 'split-middle', 'split-last', 'edit']
    : ['create-single', 'edit'];
  return keys.map((value) => ({ value, label: OPTION_LABELS[value] }));
}

/**
 * 고른 상황이 이 템플릿에서 더 이상 가능하지 않으면(분할 축을 방금 껐다) 단건으로 접는다.
 *
 * select의 `value`도 이걸 거쳐야 한다 — 목록에 없는 값을 그대로 주면 브라우저가 아무 option도
 * 고르지 못해 칸이 **빈칸**이 되고, 그때 미리보기는 단건을 그리고 있다(보이는 것과 그려지는
 * 것이 갈린다). 저작자의 선택 상태를 되감는 부수효과 없이 표시만 맞춘다.
 */
export function clampPreviewSubmitSituationKey(
  key: PreviewSubmitSituationKey,
  policy: TemplatePolicy | undefined,
): PreviewSubmitSituationKey {
  const isSplit = key === 'split-middle' || key === 'split-last';
  return isSplit && !getSplitAxisProvider(policy) ? 'create-single' : key;
}

/**
 * 상황 키 + 편집 중 정책 → 셸에 넘길 `ComposeSubmitSituation`.
 *
 * `mode`는 셸 prop이 정본이므로(ComposeWritePageShell JSDoc) 호출부가 이 객체의 mode를 셸에도
 * 같이 넘겨야 visibleIn 해소와 라벨이 같은 모드를 본다.
 */
export function resolvePreviewSubmitSituation(
  key: PreviewSubmitSituationKey,
  policy: TemplatePolicy | undefined,
): ComposeSubmitSituation {
  const effective = clampPreviewSubmitSituationKey(key, policy);
  if (effective === 'edit') return { mode: 'edit' };

  // 흐름 CTA는 선언에서만 온다. 중간 건에 접미를 붙이지 않는 규칙은 해소 함수(shared)의 몫이라
  // 여기서 미리 지우지 않는다 — 그 규칙을 두 곳에서 흉내 내면 한쪽만 고쳐지는 날이 온다.
  const postSubmitCtaLabel = findPostSubmitFlow(getPostSubmitFlow(policy))?.ctaLabel;
  if (effective === 'create-single') return { mode: 'create', postSubmitCtaLabel };

  return {
    mode: 'create',
    splitProgress: {
      completedAfterSave: effective === 'split-last' ? PREVIEW_SPLIT_SIMULATED_TOTAL : 1,
      total: PREVIEW_SPLIT_SIMULATED_TOTAL,
    },
    postSubmitCtaLabel,
  };
}
